import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Doc, Id } from '../../../../convex/_generated/dataModel';
import { Button } from '../components/ui/button';
import { Loading } from '../components/common';
import { useLab } from '../lib/service';
import {
  CATEGORY_BRIEF,
  KIDS_CATEGORIES,
  KIDS_LANGUAGES,
  KIDS_STYLES,
  LANGUAGE_NAMES,
  STYLE_LOOK,
  type KidsCategory,
  type KidsLanguage,
  type KidsStyle,
} from '../../../../packages/domain/src/kids';
import './kids.css';

/**
 * CLAPTAL Kids in the Lab: the kids series, the ideas children and parents send, and how the app
 * is used. A kids series is produced in the ordinary series studio, with the kids prompts; this
 * screen creates it and the studio's CLAPTAL Kids panel publishes its episodes.
 */
const categoryName = (c: KidsCategory) => CATEGORY_BRIEF[c].split(':')[0];
type Draft = {
  title: string;
  idea: string;
  language: KidsLanguage;
  style: KidsStyle;
  category: KidsCategory;
  ageMin: number;
  ageMax: number;
  sourceIdeaId?: Id<'kidsIdeas'>;
};
const EMPTY: Draft = {
  title: '',
  idea: '',
  language: 'fr',
  style: '3d',
  category: 'science',
  ageMin: 6,
  ageMax: 8,
};

export function Kids({ onError }: { onError: (e: unknown) => void }) {
  const lab = useLab();
  if (lab.isDemo)
    return (
      <section className="settings-card">
        <h2>CLAPTAL Kids</h2>
        <p>CLAPTAL Kids is not part of the demo workspace.</p>
      </section>
    );
  return <KidsLive onError={onError} />;
}

function KidsLive({ onError }: { onError: (e: unknown) => void }) {
  const data = useQuery(api.kidsAdmin.overview);
  const create = useMutation(api.kidsAdmin.createSeries),
    setState = useMutation(api.kidsAdmin.setSeriesState),
    translate = useMutation(api.kidsAdmin.translate),
    review = useMutation(api.kidsAdmin.reviewIdea),
    archive = useMutation(api.kidsAdmin.archiveIdea);
  const [draft, setDraft] = useState<Draft | null>(null),
    [busy, setBusy] = useState(false),
    [created, setCreated] = useState<Id<'projects'> | null>(null);
  const writable = useLab().home?.access.role !== 'reviewer';
  if (!data) return <Loading />;
  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }
  const fromIdea = (idea: Doc<'kidsIdeas'>) =>
    setDraft({
      ...EMPTY,
      title: idea.title,
      idea: idea.premise,
      language: idea.language,
      sourceIdeaId: idea._id,
    });
  return (
    <div className="kids-admin">
      <div className="series-page-heading">
        <div>
          <div className="eyebrow">CLAPTAL KIDS</div>
          <h1>Kids studio</h1>
          <p>
            Series for children aged 3–12, produced with the kids prompts and published only to
            CLAPTAL Kids.
          </p>
        </div>
        <div className="series-heading-actions">
          <Button disabled={!writable} onClick={() => setDraft({ ...EMPTY })}>
            New kids series
          </Button>
        </div>
      </div>

      {created && (
        <p className="kids-note" role="status">
          Series created.{' '}
          <Link to="/series/$projectId" params={{ projectId: created }}>
            Open it in the series studio
          </Link>{' '}
          and choose a Pipeline 2 profile to develop it.
        </p>
      )}

      {draft && (
        <section className="settings-card kids-form" aria-label="New kids series">
          <h2>{draft.sourceIdeaId ? 'Make a series from this idea' : 'New kids series'}</h2>
          <label>
            Title
            <input
              value={draft.title}
              maxLength={120}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </label>
          <label>
            Idea
            <textarea
              rows={4}
              value={draft.idea}
              maxLength={4000}
              onChange={(e) => setDraft({ ...draft, idea: e.target.value })}
            />
          </label>
          <div className="kids-row">
            <label>
              Language
              <select
                value={draft.language}
                onChange={(e) => setDraft({ ...draft, language: e.target.value as KidsLanguage })}
              >
                {KIDS_LANGUAGES.map((l) => (
                  <option key={l} value={l}>
                    {LANGUAGE_NAMES[l]}
                    {l === 'ar' ? ' (fusha)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Style
              <select
                value={draft.style}
                onChange={(e) => setDraft({ ...draft, style: e.target.value as KidsStyle })}
              >
                {KIDS_STYLES.map((s) => (
                  <option key={s} value={s}>
                    {STYLE_LOOK[s].name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Category
              <select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value as KidsCategory })}
              >
                {KIDS_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {categoryName(c)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Ages from
              <input
                type="number"
                min={3}
                max={12}
                value={draft.ageMin}
                onChange={(e) => setDraft({ ...draft, ageMin: Number(e.target.value) })}
              />
            </label>
            <label>
              to
              <input
                type="number"
                min={3}
                max={12}
                value={draft.ageMax}
                onChange={(e) => setDraft({ ...draft, ageMax: Number(e.target.value) })}
              />
            </label>
          </div>
          <div className="kids-actions">
            <Button
              disabled={busy || !draft.title.trim() || draft.idea.trim().length < 20}
              onClick={() =>
                void run(async () => {
                  const result = await create(draft);
                  setCreated(result.projectId);
                  setDraft(null);
                })
              }
            >
              Create series
            </Button>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </section>
      )}

      <section className="settings-card" aria-labelledby="kids-series">
        <h2 id="kids-series">Series</h2>
        {!data.series.length ? (
          <p>No kids series yet. Create one, or make one from a children’s idea below.</p>
        ) : (
          <ul className="kids-list">
            {data.series.map((s) => (
              <li key={s._id}>
                <div>
                  <Link to="/series/$projectId" params={{ projectId: s.projectId }}>
                    <strong>{s.title}</strong>
                  </Link>
                  <small>
                    {LANGUAGE_NAMES[s.language]} · {STYLE_LOOK[s.style].name} ·{' '}
                    {categoryName(s.category)} · ages {s.ageMin}–{s.ageMax} · {s.published}{' '}
                    published · {s.state}
                    {s.translationOf ? ' · translation' : ''}
                  </small>
                </div>
                <div className="kids-actions">
                  <select
                    aria-label={`Translate ${s.title}`}
                    disabled={busy || !writable}
                    value=""
                    onChange={(e) =>
                      void run(async () => {
                        const result = await translate({
                          seriesId: s._id,
                          language: e.target.value as KidsLanguage,
                        });
                        setCreated(result.projectId);
                      })
                    }
                  >
                    <option value="">Translate…</option>
                    {KIDS_LANGUAGES.filter((l) => l !== s.language).map((l) => (
                      <option key={l} value={l}>
                        {LANGUAGE_NAMES[l]}
                      </option>
                    ))}
                  </select>
                  {s.state === 'removed' ? (
                    <Button
                      variant="outline"
                      disabled={busy || !writable}
                      onClick={() => void run(() => setState({ seriesId: s._id, state: 'draft' }))}
                    >
                      Restore
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      disabled={busy || !writable}
                      onClick={() =>
                        window.confirm(`Take “${s.title}” off CLAPTAL Kids?`) &&
                        void run(() => setState({ seriesId: s._id, state: 'removed' }))
                      }
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="settings-card" aria-labelledby="kids-review">
        <h2 id="kids-review">Parents’ ideas to review</h2>
        <p>Written ideas are read here before children can see and vote on them.</p>
        {!data.review.length ? (
          <p>Nothing to review.</p>
        ) : (
          <ul className="kids-list">
            {data.review.map((idea) => (
              <li key={idea._id}>
                <div>
                  <strong>{idea.title}</strong>
                  <small>
                    {LANGUAGE_NAMES[idea.language]} · {idea.premise}
                  </small>
                </div>
                <div className="kids-actions">
                  <Button
                    disabled={busy || !writable}
                    onClick={() => void run(() => review({ ideaId: idea._id, approve: true }))}
                  >
                    Open to votes
                  </Button>
                  <Button
                    variant="outline"
                    disabled={busy || !writable}
                    onClick={() => void run(() => review({ ideaId: idea._id, approve: false }))}
                  >
                    Archive
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="settings-card" aria-labelledby="kids-votes">
        <h2 id="kids-votes">Ideas children are voting on · {data.round}</h2>
        {!data.open.length ? (
          <p>No open ideas.</p>
        ) : (
          <ul className="kids-list">
            {data.open.map((idea) => (
              <li key={idea._id}>
                <div>
                  <strong>
                    {idea.title} · {idea.votes} vote{idea.votes === 1 ? '' : 's'}
                  </strong>
                  <small>
                    {idea.round} · {idea.kind === 'child' ? 'a child’s cards' : 'a parent'} ·{' '}
                    {LANGUAGE_NAMES[idea.language]} · {idea.premise}
                  </small>
                </div>
                <div className="kids-actions">
                  <Button disabled={busy || !writable} onClick={() => fromIdea(idea)}>
                    Make a series
                  </Button>
                  <Button
                    variant="outline"
                    disabled={busy || !writable}
                    onClick={() => void run(() => archive({ ideaId: idea._id }))}
                  >
                    Archive
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="settings-card" aria-labelledby="kids-usage">
        <h2 id="kids-usage">Usage · last 7 days</h2>
        <p>
          First-party events from the kids app, kept in CLAPTAL’s database ({data.families}{' '}
          {data.families === 1 ? 'family' : 'families'}). The app also reports to PostHog, which
          will be self-hosted for production.
        </p>
        <table className="kids-usage">
          <thead>
            <tr>
              <th scope="col">Event</th>
              <th scope="col">iOS</th>
              <th scope="col">Android</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.usage).length ? (
              Object.entries(data.usage).map(([name, count]) => (
                <tr key={name}>
                  <td>{name.replace(/_/g, ' ')}</td>
                  <td>{count.ios}</td>
                  <td>{count.android}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3}>No events yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
